import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faMoneyBill,
  faBell,
  faMessage,
  faChevronDown,
  faChevronUp,
  faTrash,
  faAdd,
  faSpinner,
  faArrowDown,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import Notifications from '../../components/ManagerDashboard/Notifications';
import styles from './DatesScreen.module.css';

// Delete confirmation modal component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, tourName, isProcessing }) => {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>Delete Tour</h2>
        <p className={styles.modalMessage}>
          Are you sure you want to delete the tour "{tourName}"? This will remove all data including schedules, files, and tickets for all members. This action cannot be undone.
        </p>
        <div className={styles.modalButtons}>
          <button 
            className={`${styles.modalButton} ${styles.cancelButton}`}
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button 
            className={`${styles.modalButton} ${styles.deleteButton}`}
            onClick={onConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DatesScreen = () => {
  const {
    selectedTour,
    setSelectedTour,
    tours,
    selectedDate,
    setSelectedDate,
    fetchToursData,
    loadMoreTours,
    hasMoreTours,
    deleteTour,
    updateTourData,
    isLoading
  } = useContext(TourContext);

  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  
  // State declarations
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredTourDates, setFilteredTourDates] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [componentLoading, setComponentLoading] = useState(true);
  // Add state variables for tour deletion
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tourToDelete, setTourToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingTourDates, setIsLoadingTourDates] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const dropdownRef = useRef(null);

  // Click outside handler for dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  // Add a useEffect to fetch initial data when the component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      if (userData && tours.length === 0) {
        console.log("DatesScreen: Fetching initial tours data");
        await fetchToursData(userData, 1);
        console.log("DatesScreen: Initial tours fetch completed");
      }
    };
    
    fetchInitialData();
  }, [userData, fetchToursData, tours.length]);

  // Add null checks for all userData access in filtering function
  useEffect(() => {
    console.log("=== FILTERING TOUR DATES ===");
    console.log("selectedTour:", selectedTour?.id);
    console.log("userData:", userData?.userId, userData?.userType);
    
    setIsLoadingTourDates(true);
    
    if (!selectedTour || !userData) {
      console.log("No selectedTour or userData, clearing filteredTourDates");
      setFilteredTourDates([]);
      setIsLoadingTourDates(false);
      return;
    }
    
    // Add a small delay to ensure tour dates have been loaded
    // This helps resolve the race condition when dates aren't immediately available
    const delayedFiltering = setTimeout(() => {
      console.log("Delayed tour date filtering running");
      console.log("Selected tour dates:", selectedTour.tourDates?.length || 0);
      
      // Safety check for tourDates
      if (!selectedTour.tourDates || selectedTour.tourDates.length === 0) {
        console.log("No tour dates available");
        setFilteredTourDates([]);
        setIsLoadingTourDates(false);
        return;
      }
      
      // Add detailed logging to inspect the tour structure
    console.log("Tour creator:", selectedTour.createdBy?.userId);
    console.log("Current user:", userData.userId);
    console.log("Is creator match:", selectedTour.createdBy?.userId === userData.userId);
    
    if (selectedTour.managerDetails) {
      console.log("Manager details count:", selectedTour.managerDetails.length);
      const userManager = selectedTour.managerDetails.find(m => m.userId === userData.userId);
      console.log("User is manager:", !!userManager);
      if (userManager) {
        console.log("Manager assigned dates:", userManager.assignedDates?.length || 0);
      }
    }
      
      // If user is the tour creator, show all dates
      if (selectedTour.createdBy && selectedTour.createdBy.userId === userData.userId) {
        console.log("User is the tour creator, showing all dates");
        setFilteredTourDates(selectedTour.tourDates || []);
        setIsLoadingTourDates(false);
        return;
      }
      
      // For vendors, only show dates they are assigned to with 'accepted' status
      if (userData.userType === 'Vendor') {
        console.log("User is a vendor, filtering for accepted dates only");
      
        // Find vendor entry in vendorDetails
        const vendorEntry = selectedTour.vendorDetails?.find(
          vendor => vendor.userId === userData.userId
        );
        
        if (vendorEntry && vendorEntry.assignedDates && vendorEntry.assignedDates.length > 0) {
          console.log("Found vendor entry with assigned dates:", vendorEntry.assignedDates.length);
          
          // Strictly filter to only include dates with explicitly "accepted" status
          // No fallback for missing status - must be "accepted"
          const acceptedDateIds = vendorEntry.assignedDates
            .filter(date => date.status === 'accepted')
            .map(date => date.tourDateDocId);
          
          console.log("Accepted date IDs:", acceptedDateIds);
          
          // If no accepted dates, show empty
          if (acceptedDateIds.length === 0) {
            console.log("No accepted dates found for vendor");
            setFilteredTourDates([]);
            setIsLoadingTourDates(false);
            return;
          }
          
          // Filter tour dates to only include those with accepted status
          const filteredDates = selectedTour.tourDates.filter(date =>
            acceptedDateIds.includes(date.id)
          );
          
          console.log("Filtered dates count for vendor:", filteredDates.length);
          setFilteredTourDates(filteredDates);
          setIsLoadingTourDates(false);
          return;
        } else {
          console.log("Vendor not assigned to this tour or no assigned dates");
          setFilteredTourDates([]);
          setIsLoadingTourDates(false);
          return;
        }
      }
      
      // For artists, show their assigned dates
      if (userData.userType === 'Artist') {
        console.log("User is an artist, filtering by assigned dates");
        
        // Find artist entry in artistDetails
        const artistEntry = selectedTour.artistDetails?.find(
          artist => artist.userId === userData.userId
        );
        
        if (artistEntry && artistEntry.assignedDates && artistEntry.assignedDates.length > 0) {
          console.log("Found artist entry with assigned dates:", artistEntry.assignedDates.length);
          
          // Get the assigned date IDs
          const assignedDateIds = artistEntry.assignedDates.map(date => date.tourDateDocId);
          console.log("Assigned date IDs:", assignedDateIds);
          
          // Filter tour dates to only include those assigned to this artist
          const filteredDates = selectedTour.tourDates.filter(date => 
            assignedDateIds.includes(date.id)
          );
          
          console.log("Filtered dates count for artist:", filteredDates.length);
          setFilteredTourDates(filteredDates);
        } else {
          console.log("Artist not assigned to this tour - showing no dates");
          setFilteredTourDates([]);
        }
        setIsLoadingTourDates(false);
        return;
      }
      
      // For crew, show their assigned dates
      if (userData.userType === 'Crew') {
        console.log("User is crew, filtering by assigned dates");
        
        // Find crew entry in crewDetails
        const crewEntry = selectedTour.crewDetails?.find(
          crew => crew.userId === userData.userId
        );
        
        if (crewEntry && crewEntry.assignedDates && crewEntry.assignedDates.length > 0) {
          console.log("Found crew entry with assigned dates:", crewEntry.assignedDates.length);
          
          // Get the assigned date IDs
          const assignedDateIds = crewEntry.assignedDates.map(date => date.tourDateDocId);
          console.log("Assigned date IDs:", assignedDateIds);
          
          // Filter tour dates to only include those assigned to this crew member
          const filteredDates = selectedTour.tourDates.filter(date => 
            assignedDateIds.includes(date.id)
          );
          
          console.log("Filtered dates count for crew:", filteredDates.length);
          setFilteredTourDates(filteredDates);
        } else {
          console.log("Crew not assigned to this tour - showing no dates");
          setFilteredTourDates([]);
        }
        setIsLoadingTourDates(false);
        return;
      }
      
      // For staff, show their assigned dates
      if (userData.userType === 'Staff') {
        console.log("User is staff, filtering by assigned dates");
        
        // Find staff entry in staffDetails
        const staffEntry = selectedTour.staffDetails?.find(
          staff => staff.userId === userData.userId
        );
        
        if (staffEntry && staffEntry.assignedDates && staffEntry.assignedDates.length > 0) {
          console.log("Found staff entry with assigned dates:", staffEntry.assignedDates.length);
          
          // Get the assigned date IDs
          const assignedDateIds = staffEntry.assignedDates.map(date => date.tourDateDocId);
          console.log("Assigned date IDs:", assignedDateIds);
          
          // Filter tour dates to only include those assigned to this staff member
          const filteredDates = selectedTour.tourDates.filter(date => 
            assignedDateIds.includes(date.id)
          );
          
          console.log("Filtered dates count for staff:", filteredDates.length);
          setFilteredTourDates(filteredDates);
        } else {
          console.log("Staff not assigned to this tour - showing no dates");
          setFilteredTourDates([]);
        }
        setIsLoadingTourDates(false);
        return;
      }
      
      // For managers, filter by their assigned dates
      if (userData.userType === 'Manager') {
        console.log("User is a manager, filtering by assigned dates");
        const managerEntry = selectedTour.managerDetails?.find(
          manager => manager.userId === userData.userId
        );
        
        if (managerEntry && managerEntry.assignedDates && managerEntry.assignedDates.length > 0) {
          console.log("Found manager entry with assigned dates:", managerEntry.assignedDates.length);
          
          // Get the assigned date IDs
          const assignedDateIds = managerEntry.assignedDates.map(date => date.tourDateDocId);
          console.log("Assigned date IDs:", assignedDateIds);
          
          // Filter tour dates to only include those assigned to this manager
          const filteredDates = selectedTour.tourDates.filter(date => 
            assignedDateIds.includes(date.id) || 
            managerEntry.assignedDates.some(
              assignedDate => assignedDate.date === date.date
            )
          );
          
          console.log("Filtered dates count for manager:", filteredDates.length);
          setFilteredTourDates(filteredDates);
        } else {
          console.log("Manager not assigned to this tour - showing no dates");
          setFilteredTourDates([]);
        }
        setIsLoadingTourDates(false);
        return;
      }
      
      // For any other user types, show no dates as they aren't assigned to the tour
      console.log("User is not assigned to this tour, showing no dates");
      setFilteredTourDates([]);
      setIsLoadingTourDates(false);
      
    }, 500); // 500ms delay to ensure tour dates have loaded
    
    // Cleanup function
    return () => {
      clearTimeout(delayedFiltering);
    };
  }, [selectedTour, userData]);

  // Update initial data loading logic
  useEffect(() => {
    console.log("DatesScreen mounting, checking for tours");
    
    const fetchInitialTours = async () => {
      if (!userData) {
        console.log("DatesScreen: No user data available yet");
        return;
      }
      
      if (tours.length === 0) {
        console.log("DatesScreen: No tours loaded, fetching initial tour data");
        setIsLoadingMore(true);
        try {
          await fetchToursData(userData, 1);
          console.log("DatesScreen: Initial tours fetch completed");
          
          // Add an additional check - if a tour is selected but has no dates, force a refresh
          if (selectedTour && (!selectedTour.tourDates || selectedTour.tourDates.length === 0)) {
            console.log("DatesScreen: Selected tour has no dates, forcing refresh");
            // Use a slight delay to avoid race conditions
            setTimeout(async () => {
              try {
                await updateTourData(selectedTour.id);
                console.log("DatesScreen: Forced tour refresh completed");
              } catch (err) {
                console.error("Error in forced tour refresh:", err);
              }
            }, 1000);
          }
        } catch (error) {
          console.error('Error fetching initial tour data:', error);
        } finally {
          setIsLoadingMore(false);
        }
      } else if (selectedTour && (!selectedTour.tourDates || selectedTour.tourDates.length === 0)) {
        // Even if tours are loaded but selected tour has no dates, force an update
        console.log("DatesScreen: Tours loaded but selected tour has no dates, updating tour data");
        try {
          await updateTourData(selectedTour.id);
          console.log("DatesScreen: Tour update completed");
        } catch (error) {
          console.error('Error updating tour data:', error);
        }
      } else {
        console.log("DatesScreen: Tours already loaded");
      }
    };
    
    fetchInitialTours();
  }, [userData, tours, fetchToursData, selectedTour, updateTourData]);

  // Add useEffect to handle initial loading state
  useEffect(() => {
    // Consider component loaded when we have userData and tours are loaded
    if (!isLoading && (userData || tours.length > 0)) {
      setComponentLoading(false);
    }
  }, [isLoading, userData, tours]);

  // Add early return for loading state
  if (componentLoading || !userData) {
    return (
      <div className={styles.datesScreen}>
        <div className={styles.loadingContainer}>
          <FontAwesomeIcon icon={faSpinner} spin />
          <p>Loading tour data...</p>
        </div>
      </div>
    );
  }

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    
    // Get day of week (e.g., "Mon")
    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
    
    // Get month and day (e.g., "Aug 6")
    const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    // Get year (e.g., "2024")
    const year = date.getFullYear();
    
    return { dayOfWeek, monthDay, year };
  };

  // Handle date selection
  const handleDateClick = (tourDate) => {
    console.log("Date clicked:", tourDate.id);
    setSelectedDate(tourDate.id);
    
    // Change the navigation based on user type
    if (userData.userType === 'Vendor' || userData.userType === 'Staff') {
      navigate(`/tour/${selectedTour.id}/${tourDate.id}`, { 
        state: { activeTab: 'event' } 
      });
    } else {
      navigate(`/tour/${selectedTour.id}/${tourDate.id}`);
    }
  };

  // Update the handleSelectTour function in DatesScreen.js
  const handleSelectTour = (tour, e) => {
    // Stop event propagation to prevent clicking through to elements below
    if (e) {
      e.stopPropagation();
    }
    
    // Update the selected tour
    setSelectedTour(tour);
    
    // Close the dropdown
    setIsDropdownOpen(false);
    
    // Don't automatically select a date or navigate
    // The user should explicitly select a date after selecting a tour
    
    // Only set the selectedDate to null if no dates are available
    if (!tour.tourDates || tour.tourDates.length === 0) {
      setSelectedDate(null);
    }
    
    // No automatic navigation - stay on the current screen
  };

  // Handle load more
  const handleLoadMore = async () => {
    if (isLoadingMore) return;
    
    setIsLoadingMore(true);
    try {
      await loadMoreTours(userData);
    } catch (error) {
      console.error('Error loading more tours:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle creating a new tour
  const handleCreateTour = () => {
    navigate('/create-tour');
  };

  // Handle editing the current tour
  const handleEditTour = () => {
    if (selectedTour) {
      navigate('/create-tour', { state: { tourData: selectedTour } });
    }
  };

  // Navigate to expenses
  const handleExpenses = () => {
    navigate('/expense');
  };

  // Navigate to messages
  const handleMessages = () => {
    navigate('/messages');
  };

  // Navigate to notifications
  const handleNotifications = () => {
    setShowNotifications(true);
  };

  // Add a function to close notifications
  const handleCloseNotifications = () => {
    setShowNotifications(false);
  };


  // Handle tour deletion
  const handleDeleteTour = (tour, e) => {
    // Stop event propagation
    if (e) {
      e.stopPropagation();
    }
    
    // First check if the current user is the creator
    if (tour.createdBy?.userId !== userData.userId) {
      alert('Only the creator of this tour can delete it.');
      return;
    }
    
    // Set the tour to delete and show the confirmation modal
    setTourToDelete(tour);
    setShowDeleteModal(true);
  };

  // Handle tour deletion confirmation
  const confirmDeleteTour = async () => {
    if (!tourToDelete) return;
    
    setIsDeleting(true);
    try {
      console.log("Deleting tour:", tourToDelete.id);
      // Use the centralized deleteTour function from TourContext
      const success = await deleteTour(tourToDelete.id, userData?.userId);
      
      if (success) {
        console.log("Tour deleted successfully");
        
        // If we deleted the currently selected tour, navigate back to profile
        if (selectedTour?.id === tourToDelete.id) {
          console.log("Deleted the currently selected tour, navigating back to profile");
          navigate(`/${userData.userName}`);
        }
      } else {
        alert('Failed to delete tour. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting tour:', error);
      alert('Failed to delete tour. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setTourToDelete(null);
    }
  };

  // Sort tours by creation date (newest first)
  const sortedTours = [...tours].sort((a, b) => {
    // First check for createdAt timestamp
    if (a.createdAt && b.createdAt) {
      const aDate = a.createdAt.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt);
      const bDate = b.createdAt.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt);
      return bDate - aDate; // Descending (newest first)
    }
    
    // Fallback to tour dates if available
    if (a.tourDates?.length && b.tourDates?.length) {
      const aFirstDate = new Date(a.tourDates[0].date);
      const bFirstDate = new Date(b.tourDates[0].date);
      return bFirstDate - aFirstDate; // Descending (newest first)
    }
    
    // Default to 0 (no change)
    return 0;
  });

  return (
    <div className={styles.datesScreen}>
      {/* Header with actions */}
      <div className={styles.header}>
        <div className={styles.leftActions}>
        <button className={styles.iconButton} onClick={handleEditTour}>
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button className={styles.iconButton} onClick={handleExpenses}>
            <FontAwesomeIcon icon={faMoneyBill} />
          </button>
        </div>
        
        <div className={styles.tourSelector} ref={dropdownRef}>
          <button 
            className={styles.tourSelectorButton}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <div className={styles.tourInfo}>
              <span>{selectedTour?.actName || 'Select Tour'}</span>
              <span className={styles.tourName}>{selectedTour?.tourName}</span>
            </div>
            <FontAwesomeIcon icon={isDropdownOpen ? faChevronUp : faChevronDown} />
          </button>
          
          {isDropdownOpen && (
            <div className={styles.tourDropdown}>
              {sortedTours.map(tour => (
                <div 
                  key={tour.id}
                  className={`${styles.tourItem} ${selectedTour?.id === tour.id ? styles.selected : ''}`}
                  onClick={(e) => handleSelectTour(tour, e)}
                >
                  <div>
                    <div className={styles.tourActName}>{tour.actName}</div>
                    <div className={styles.tourNameSmall}>{tour.tourName}</div>
                  </div>
                  
                  {/* Only show delete button if user is the creator */}
                  {selectedTour?.id === tour.id && tour.createdBy?.userId === userData.userId && (
                    <div className={styles.trashIcon} onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTour(tour, e);
                    }}>
                      <FontAwesomeIcon icon={faTrash} />
                    </div>
                  )}
                </div>
              ))}
              
              {/* Load More Button */}
              {hasMoreTours && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLoadMore();
                  }}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faArrowDown} />
                      <span>Load More</span>
                    </>
                  )}
                </button>
              )}
              
              {/* Create Tour Button */}
              <button 
                className={styles.createTourButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateTour();
                }}
                disabled={
                  (userData.membershipTier === 'Free' && tours.length >= 1) ||
                  (userData.membershipTier === 'Basic' && tours.length >= 10)
                }
              >
                <FontAwesomeIcon icon={faAdd} />
                <span>Create New Tour</span>
              </button>
            </div>
          )}
        </div>
        
        <div className={styles.rightActions}>
    
          <button className={styles.iconButton} onClick={handleNotifications}>
            <FontAwesomeIcon icon={faBell} />
          </button>
          <button className={styles.iconButton} onClick={handleMessages}>
            <FontAwesomeIcon icon={faMessage} />
          </button>
        </div>
      </div>
      
      {/* Tour Dates */}
      <div className={styles.datesList}>
  {isLoadingTourDates ? (
    <div className={styles.loadingDates}>
      <FontAwesomeIcon icon={faSpinner} spin />
      <p>Loading tour dates...</p>
    </div>
  ) : filteredTourDates.length > 0 ? (
    filteredTourDates
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((tourDate) => {
        const isRescheduled = tourDate.rescheduleDate?.isRescheduled;
        const rescheduledDate = isRescheduled ? new Date(tourDate.rescheduleDate.newDate) : null;
        const isCancelled = tourDate.cancelled;
        
        // Display rescheduled date if available, otherwise show original date
        const displayDate = isRescheduled ? rescheduledDate : new Date(tourDate.date);
        const { dayOfWeek, monthDay, year } = formatDate(displayDate);

        return (
          <div 
            key={tourDate.id}
            className={`${styles.dateCard} ${selectedDate === tourDate.id ? styles.selected : ''} ${isCancelled ? styles.cancelled : ''} ${isRescheduled ? styles.rescheduled : ''}`}
            onClick={() => handleDateClick(tourDate)}
          >
                  <div className={styles.dateInfo}>
                    <div className={styles.dateColumn}>
                      <div className={styles.dateDay}>{dayOfWeek}</div>
                      <div className={styles.dateValue}>
                        {monthDay} {year}
                      </div>
                    </div>
                    
                    <div className={styles.venueInfo}>
                      {tourDate.event?.venueDetails?.venueData && (
                        <>
                          <div className={styles.venueName}>
                            {tourDate.event.venueDetails.venueData.displayName}
                          </div>
                          <div className={styles.venueAddress}>
                            {tourDate.event.venueDetails.venueData.formattedAddress}
                          </div>
                          {isRescheduled && (
                            <div className={styles.rescheduledInfo}>
                              Rescheduled from: {formatDate(new Date(tourDate.date)).monthDay}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
        ) : (
          <div className={styles.noDateMessage}>
            {userData && userData.userType === 'Manager' ? 
              'You don\'t have access to any dates for this tour.' : 
              'No dates available for this tour.'}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteConfirmationModal 
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDeleteTour}
          tourName={tourToDelete?.tourName}
          isProcessing={isDeleting}
        />
      )}
 {showNotifications && (
        <>
          <div className={styles.notificationsOverlay} onClick={handleCloseNotifications} />
          <Notifications onClose={handleCloseNotifications} />
        </>
      )}
    </div>
  );
};


export default DatesScreen;